<template>
  <v-card class="chat-window" elevation="8">
    <header class="chat-window__header">
      <div class="chat-window__header-left">
        <button
          class="chat-window__header-btn chat-window__header-btn--ghost"
          aria-label="Toggle conversation history"
          @click="$emit('toggle:history')"
        >
          <v-icon size="20">mdi-history</v-icon>
        </button>

        <div class="chat-window__header-logo">
          <span class="chat-window__header-logo-icon">🌵</span>
        </div>

        <div class="chat-window__header-title-wrapper">
          <h1 class="chat-window__header-title">Slick Cactus AI Agent</h1>
          <p class="chat-window__header-subtitle">Real Estate CRM Assistant</p>
        </div>
      </div>

      <div class="chat-window__header-right">
        <button
          class="chat-window__header-btn chat-window__header-btn--primary"
          aria-label="New conversation"
          @click="$emit('create:conversation')"
        >
          <v-icon size="20" color="white">mdi-plus</v-icon>
        </button>

        <button
          class="chat-window__header-btn chat-window__header-btn--ghost"
          aria-label="Close chat"
          @click="$emit('close:window')"
        >
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>
    </header>

    <!-- Messages Area -->
    <div ref="messagesContainer" class="chat-window__messages">
      <div v-if="isLoadingMessages" class="chat-window__loading">
        <v-progress-circular
          indeterminate
          color="primary"
          size="32"
        />
        <p class="mt-3 text-medium-emphasis">Loading conversation...</p>
      </div>

      <div v-else-if="messages.length === 0" class="chat-window__empty">
        <v-icon size="64" color="grey-lighten-1">mdi-chat-outline</v-icon>
        <p class="mt-4 text-medium-emphasis">
          Ask me anything about properties and pipelines!
        </p>
        <p class="text-medium-emphasis caption">
          Try: "Find office buildings in Dallas"
        </p>
      </div>

      <template
        v-for="(message, index) in messages"
        :key="`separator-${message.id}`"
      >
        <div
          v-if="shouldShowDateSeparator(index)"
          class="chat-window__date-separator"
        >
          <span>{{ getDateSeparatorText(message.timestamp) }}</span>
        </div>

        <AiChatMessage
          :message="message"
        />
      </template>

      <!-- Typing indicator -->
      <div v-if="isTyping" class="chat-window__typing">
        <v-avatar color="secondary" size="32">
          <v-icon size="small" color="white">mdi-robot</v-icon>
        </v-avatar>
        <div class="chat-window__typing-indicator">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <v-divider />
    <div class="chat-window__input">
      <v-textarea
        v-model="inputMessage"
        placeholder="Type your message..."
        rows="2"
        auto-grow
        hide-details
        class="chat-window__textarea"
        @keydown.enter.exact.prevent="sendMessage"
        @keydown.enter.shift.exact="inputMessage += '\n'"
      />

      <v-btn
        color="primary"
        :disabled="!inputMessage.trim() || isTyping"
        class="chat-window__send-btn"
        @click="sendMessage"
      >
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { IAiMessage } from '@/bundles/AiAgent/types';
import { formatDateSeparator, isSameDay } from '@/bundles/AiAgent/utils/formatDateSeparator';

import AiChatMessage from '@/bundles/AiAgent/components/AiChatMessage.vue';

interface IAiChatWindowInstance {
  inputMessage: string;
}

export default defineComponent({
  name: 'AiChatWindow',

  components: {
    AiChatMessage,
  },

  props: {
    messages: {
      type: Array as () => IAiMessage[],
      default: () => [],
    },
    isTyping: Boolean,
    isLoadingMessages: Boolean,
  },

  emits: ['close:window', 'create:conversation', 'send:message', 'toggle:history'],

  data: ():IAiChatWindowInstance => ({
    inputMessage: '',
  }),

  watch: {
    messages: {
      handler () {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      },
      deep: true,
    },
  },

  methods: {
    sendMessage () {
      const message = this.inputMessage.trim();
      if (!message || this.isTyping) return;

      this.$emit('send:message', message);
      this.inputMessage = '';
    },

    scrollToBottom () {
      const container = this.$refs.messagesContainer as HTMLElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    shouldShowDateSeparator (index: number): boolean {
      if (index === 0) return true;

      const currentMessage = this.messages[index];
      const previousMessage = this.messages[index - 1];

      return !isSameDay(currentMessage.timestamp, previousMessage.timestamp);
    },

    getDateSeparatorText (date: Date): string {
      return formatDateSeparator(date);
    },
  },
});
</script>

<style lang="scss" scoped>
.chat-window {
  display: flex !important;
  flex-direction: column !important;
  width: 100%;
  height: 100%;
  border-radius: 0 !important;
  box-shadow: none !important;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 56px;
    padding: 0 16px;
    background-color: rgb(var(--v-theme-surface));
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    flex-shrink: 0;
  }

  &__header-left,
  &__header-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__header-btn {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;

    &--ghost {
      background-color: transparent;

      &:hover {
        background-color: rgba(var(--v-theme-on-surface), var(--v-hover-opacity));
      }

      .v-icon {
        color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
      }
    }

    &--primary {
      background-color: var(--v-primary-base);

      &:hover {
        background-color: var(--v-primary-darken1);
      }
    }
  }

  &__header-logo {
    width: 32px;
    height: 32px;
    background-color: var(--v-primary-base);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__header-logo-icon {
    font-size: 20px;
    line-height: 1;
  }

  &__header-title-wrapper {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__header-title {
    font-size: 14px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    line-height: 1.25;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__header-subtitle {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    line-height: 1.25;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__messages {
    flex: 1 1 auto;
    overflow-y: auto;
    background-color: rgb(var(--v-theme-surface));
    padding: 8px 0;
    min-height: 0;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(var(--v-theme-on-surface), 0.2);
      border-radius: 3px;

      &:hover {
        background: rgba(var(--v-theme-on-surface), 0.4);
      }
    }
  }

  &__empty,
  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 32px;
    text-align: center;
  }

  &__date-separator {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 16px 8px;

    span {
      font-size: 12px;
      font-weight: 500;
      color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
      background-color: rgb(var(--v-theme-sc-surface-soft));
      padding: 4px 12px;
      border-radius: 12px;
    }
  }

  &__typing {
    display: flex;
    gap: 12px;
    padding: 12px 16px;
  }

  &__typing-indicator {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 14px;
    background-color: rgb(var(--v-theme-sc-surface-soft));
    border-radius: 12px;

    span {
      width: 8px;
      height: 8px;
      background-color: rgba(var(--v-theme-on-surface), 0.4);
      border-radius: 50%;
      animation: typing 1.4s infinite;

      &:nth-child(1) {
        animation-delay: 0s;
      }

      &:nth-child(2) {
        animation-delay: 0.2s;
      }

      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }
  }

  &__input {
    display: flex;
    gap: 8px;
    padding: 12px;
    background-color: rgb(var(--v-theme-sc-surface-subtle));
    align-items: flex-end;
  }

  &__textarea {
    flex: 1;

    :deep(.v-input__control) {
      background-color: rgb(var(--v-theme-surface));
    }
  }

  &__send-btn {
    min-width: 48px !important;
    height: 48px !important;
    padding: 0 12px !important;
  }
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}
</style>
